/* ============================================================
   ToiletsEtc — Theme JS (vanilla, no framework)
   ============================================================ */
(function () {
  'use strict';

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const money = (cents) => {
    const v = (cents / 100).toFixed(2);
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  /* ==========================================================
     Cart — AJAX + drawer
     ========================================================== */
  const Cart = {
    state: null,
    listeners: new Set(),

    async fetch () {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      this.state = await r.json();
      this._emit();
      return this.state;
    },

    async add (formData) {
      // Ask for the cart-drawer section back in the SAME request so it's rendered with the item
      // already in the cart — avoids the edge race where a follow-up ?sections fetch sees a stale cart.
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ description: 'Could not add to cart' }));
        throw new Error(err.description || 'Could not add to cart');
      }
      const res = await r.json();
      await this.fetch();
      await Drawer.render(res.sections && res.sections['cart-drawer']);
      Drawer.open();
      return this.state;
    },

    async change (key, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
      });
      this.state = await r.json();
      this._emit();
      await Drawer.render(this.state.sections && this.state.sections['cart-drawer']);
      return this.state;
    },

    on (fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
    _emit () {
      this.listeners.forEach(fn => { try { fn(this.state); } catch (e) {} });
      this._updateCount();
    },
    _updateCount () {
      const count = this.state ? this.state.item_count : 0;
      $$('[data-cart-count]').forEach(el => {
        el.textContent = count;
        if (count > 0) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
    }
  };

  /* ==========================================================
     Cart drawer
     ========================================================== */
  const Drawer = {
    el: null,
    init () {
      this.el = $('[data-cart-drawer]');
      if (!this.el) return;
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-open]')) { e.preventDefault(); this.open(); }
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      this._bindLines();
    },
    open () {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },
    close () {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },
    // Render the drawer from section HTML returned by cart add/change. Falls back to a
    // (cache-busted) fetch if none was provided.
    async render (html) {
      if (!this.el) return;
      if (!html) { await this.rerender(); return; }
      this._swap(html);
    },
    async rerender () {
      if (!this.el) return;
      const r = await fetch(window.location.pathname + '?sections=cart-drawer&_=' + Date.now(), { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
      if (!r.ok) return;
      const data = await r.json();
      if (data['cart-drawer']) this._swap(data['cart-drawer']);
    },
    _swap (html) {
      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      const fresh = wrap.querySelector('[data-cart-drawer]');
      if (fresh) {
        const wasOpen = this.el.getAttribute('aria-hidden') === 'false';
        this.el.replaceWith(fresh);
        this.el = fresh;
        if (wasOpen) this.open();
        this._bindLines();
      }
    },
    _bindLines () {
      $$('[data-line-item]', this.el).forEach(line => {
        const key = line.dataset.lineKey;
        const qtyInput = line.querySelector('[data-cart-qty]');
        const decr = line.querySelector('[data-cart-decr]');
        const incr = line.querySelector('[data-cart-incr]');
        const remove = line.querySelector('[data-cart-remove]');

        if (decr) decr.addEventListener('click', () => Cart.change(key, Math.max(0, parseInt(qtyInput.value || '1', 10) - 1)));
        if (incr) incr.addEventListener('click', () => Cart.change(key, parseInt(qtyInput.value || '1', 10) + 1));
        if (qtyInput) qtyInput.addEventListener('change', () => Cart.change(key, Math.max(0, parseInt(qtyInput.value || '0', 10))));
        if (remove) remove.addEventListener('click', () => Cart.change(key, 0));
      });
    }
  };

  /* ==========================================================
     Mobile nav
     ========================================================== */
  const MobileNav = {
    init () {
      const trigger = $('[data-mobile-nav-open]');
      const overlay = $('[data-mobile-nav]');
      const close = $('[data-mobile-nav-close]');
      if (!overlay) return;
      const open = () => {
        overlay.hidden = false;
        requestAnimationFrame(() => overlay.classList.add('is-open'));
        document.body.style.overflow = 'hidden';
      };
      const shut = () => {
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(() => { overlay.hidden = true; }, 260);
      };
      if (trigger) trigger.addEventListener('click', open);
      if (close) close.addEventListener('click', shut);
      overlay.addEventListener('click', (e) => { if (e.target === overlay) shut(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) shut(); });
    }
  };

  /* ==========================================================
     Search modal + predictive search
     ========================================================== */
  const SearchModal = {
    init () {
      const trigger = $('[data-search-open]');
      const modal = $('[data-search-modal]');
      const close = $('[data-search-close]');
      const input = $('[data-search-input]');
      const results = $('[data-search-results]');
      if (!modal) return;

      const open = () => { modal.hidden = false; setTimeout(() => input && input.focus(), 0); };
      const shut = () => { modal.hidden = true; if (results) results.innerHTML = ''; if (input) input.value = ''; };

      if (trigger) trigger.addEventListener('click', open);
      if (close) close.addEventListener('click', shut);
      modal.addEventListener('click', (e) => { if (e.target === modal) shut(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) shut(); });

      if (input && results) {
        let timer = null;
        input.addEventListener('input', () => {
          clearTimeout(timer);
          const q = input.value.trim();
          if (q.length < 2) { results.innerHTML = ''; return; }
          timer = setTimeout(() => this._search(q, results), 220);
        });
      }
    },
    async _search (q, results) {
      try {
        const r = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
        const data = await r.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) {
          results.innerHTML = `<p style="padding:1rem;color:var(--color-muted);">No results for "${q}".</p>`;
          return;
        }
        results.innerHTML = products.map(p => `
          <a href="${p.url}" class="search-modal__result">
            <img src="${p.image || ''}" alt="" loading="lazy">
            <div>
              <div style="font-weight:600;">${p.title}</div>
              <div style="color:var(--color-muted);font-size:.9rem;">${p.price || ''}</div>
            </div>
          </a>
        `).join('');
      } catch (e) { /* silent */ }
    }
  };

  /* ==========================================================
     Product form (variant picker, qty, add to cart, buy now)
     ========================================================== */
  const ProductForm = {
    init () {
      const form = $('[data-product-form]');
      if (!form) return;
      // The variant picker is a SEPARATE block (a sibling of the buy-buttons form), so the option
      // radios live OUTSIDE [data-product-form]. Scope all option queries to the shared info column,
      // otherwise selecting a finish never fires (form.querySelector finds nothing).
      const scope = form.closest('[data-product-info]') || document;
      // Product JSON is only needed for live variant switching. If it's missing or malformed,
      // Add to Cart must STILL work (via AJAX) — so parse defensively and never bail on failure.
      const productJsonEl = $('[data-product-json]');
      let product = null;
      if (productJsonEl) { try { product = JSON.parse(productJsonEl.textContent); } catch (e) { product = null; } }

      const variantIdInput = form.querySelector('[data-variant-id]');
      const atcBtn = form.querySelector('[data-atc]');
      const atcLabel = form.querySelector('[data-atc-label]');
      const buyNowBtn = form.querySelector('[data-buy-now]');
      const qtyInput = form.querySelector('[data-qty-input]');
      const qtyDecr = form.querySelector('[data-qty-decr]');
      const qtyIncr = form.querySelector('[data-qty-incr]');

      if (qtyDecr) qtyDecr.addEventListener('click', () => { qtyInput.value = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1); });
      if (qtyIncr) qtyIncr.addEventListener('click', () => { qtyInput.value = parseInt(qtyInput.value || '1', 10) + 1; });

      const updateForVariant = (variant) => {
        if (!variant) {
          atcLabel.textContent = 'Unavailable';
          atcBtn.disabled = true;
          if (buyNowBtn) buyNowBtn.hidden = true;
          return;
        }
        variantIdInput.value = variant.id;
        if (variant.available) {
          atcLabel.textContent = 'Add to Cart';
          atcBtn.disabled = false;
          if (buyNowBtn) buyNowBtn.hidden = false;
        } else {
          atcLabel.textContent = 'Sold Out';
          atcBtn.disabled = true;
          if (buyNowBtn) buyNowBtn.hidden = true;
        }

        // Update price block
        const priceBlock = $('[data-product-info] .product-page__price .price');
        if (priceBlock) {
          const curr = priceBlock.querySelector('.price__current');
          if (curr) curr.textContent = money(variant.price);
          const was = priceBlock.querySelector('.price__was');
          if (was) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              was.innerHTML = '<s>' + money(variant.compare_at_price) + '</s>';
              was.style.display = '';
            } else {
              was.style.display = 'none';
            }
          }
        }

        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Update sticky ATC price
        const stickyPrice = $('[data-sticky-atc] .price__current');
        if (stickyPrice) stickyPrice.textContent = money(variant.price);

        // Update the SKU line
        const skuEl = $('[data-variant-sku]');
        if (skuEl && variant.sku) skuEl.textContent = variant.sku;

        // Update selected-value labels (option name may be a string or an object)
        product.options.forEach((opt, i) => {
          const optName = (typeof opt === 'string') ? opt : (opt && opt.name);
          if (!optName) return;
          const el = scope.querySelector(`.variant-picker__option[data-option-name="${CSS.escape(optName)}"] [data-selected-value]`);
          const val = variant['option' + (i + 1)];
          if (el && val != null) el.textContent = val;
        });
      };

      const getSelectedVariant = () => {
        const selected = product.options.map((_, i) => {
          const radio = scope.querySelector(`input[name="option-${i + 1}"]:checked`);
          return radio ? radio.value : null;
        });
        // Liquid `product | json` exposes option1/option2/option3 (NOT an `options` array),
        // so match on those — robust across Shopify's serialization shapes.
        return product.variants.find(v =>
          selected.every((val, i) => val == null || v['option' + (i + 1)] === val)
        );
      };

      if (product && product.variants) {
        scope.querySelectorAll('[data-variant-option]').forEach(radio => {
          radio.addEventListener('change', () => {
            const variant = getSelectedVariant();
            updateForVariant(variant);
          });
        });
        // Sync everything to the initially-checked finish on load.
        updateForVariant(getSelectedVariant());
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        atcBtn.disabled = true;
        const originalLabel = atcLabel.textContent;
        atcLabel.textContent = 'Adding…';
        try {
          await Cart.add(new FormData(form));
          atcLabel.textContent = 'Added ✓';
          setTimeout(() => { atcLabel.textContent = originalLabel; atcBtn.disabled = false; }, 1200);
        } catch (err) {
          atcLabel.textContent = 'Error';
          setTimeout(() => { atcLabel.textContent = originalLabel; atcBtn.disabled = false; }, 1600);
        }
      });

      if (buyNowBtn) {
        buyNowBtn.addEventListener('click', async () => {
          buyNowBtn.disabled = true;
          try {
            await Cart.add(new FormData(form));
            window.location.href = '/checkout';
          } catch (err) {
            buyNowBtn.disabled = false;
          }
        });
      }

      // Sticky ATC mirror
      const stickyBtn = $('[data-sticky-atc-btn]');
      if (stickyBtn && atcBtn) stickyBtn.addEventListener('click', () => atcBtn.click());

      // Sticky ATC visibility
      const stickyEl = $('[data-sticky-atc]');
      if (stickyEl && atcBtn) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            stickyEl.hidden = entry.isIntersecting;
          });
        }, { rootMargin: '-120px 0px 0px 0px' });
        io.observe(atcBtn);
      }
    }
  };

  /* ==========================================================
     Product gallery
     ========================================================== */
  const Gallery = {
    init () {
      const main = $('[data-gallery-main]');
      const thumbs = $$('[data-gallery-thumb]');
      if (!main || !thumbs.length) return;
      thumbs.forEach(t => {
        t.addEventListener('click', () => {
          const idx = t.dataset.index;
          $$('[data-gallery-slide]', main).forEach(s => s.classList.toggle('is-active', s.dataset.index === idx));
          thumbs.forEach(x => x.classList.toggle('is-active', x === t));
        });
      });
    }
  };

  /* ==========================================================
     Collection page — filters + sort
     ========================================================== */
  const Collection = {
    init () {
      const form = $('[data-filter-form]');
      const sort = $('[data-sort-by]');
      const toggle = $('[data-mobile-filters-toggle]');
      const filters = $('[data-collection-filters]');

      if (form) {
        form.addEventListener('change', () => this._apply(form, sort));
      }
      if (sort) {
        sort.addEventListener('change', () => this._apply(form, sort));
      }
      if (toggle && filters) {
        toggle.addEventListener('click', () => filters.classList.toggle('is-open'));
      }
    },
    _apply (form, sort) {
      const params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
      // Remove empties
      for (const [k, v] of Array.from(params.entries())) { if (!v) params.delete(k); }
      if (sort && sort.value) params.set('sort_by', sort.value);
      const base = window.location.pathname;
      const qs = params.toString();
      window.location.href = qs ? `${base}?${qs}` : base;
    }
  };

  /* ==========================================================
     Scroll reveal (light, optional)
     ========================================================== */
  const Reveal = {
    init () {
      const targets = $$('.section, .hero, .category-tile, .card-product, .testimonial, .buying-guide__card');
      if (!('IntersectionObserver' in window) || !targets.length) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.style.opacity = '1'; e.target.style.transform = 'none'; io.unobserve(e.target); }
        });
      }, { threshold: 0.08 });
      targets.forEach(t => {
        t.style.opacity = '0';
        t.style.transform = 'translateY(12px)';
        t.style.transition = 'opacity .5s ease, transform .5s ease';
        io.observe(t);
      });
    }
  };

  /* ==========================================================
     Sticky header — add shadow/solidity once scrolled
     ========================================================== */
  const StickyHeader = {
    init () {
      const header = $('[data-site-header]');
      if (!header) return;
      let ticking = false;
      const update = () => {
        header.classList.toggle('is-stuck', window.scrollY > 8);
        ticking = false;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
      }, { passive: true });
      update();
    }
  };

  /* ==========================================================
     Quick add (product cards) — adds the given variant via AJAX
     ========================================================== */
  const QuickAdd = {
    init () {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        e.preventDefault();
        const id = btn.getAttribute('data-variant-id');
        if (!id || btn.disabled) return;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const fd = new FormData();
          fd.append('id', id);
          fd.append('quantity', 1);
          await Cart.add(fd);
        } catch (err) {
          btn.textContent = 'Unavailable';
        } finally {
          setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 900);
        }
      });
    }
  };

  /* ==========================================================
     Product recommendations ("You may also like") — fetches real
     related products from the Product Recommendations API and swaps
     them in. Renders nothing when there are no recommendations.
     ========================================================== */
  const Recommendations = {
    init () {
      const el = $('[data-product-recommendations]');
      if (!el) return;
      const url = el.getAttribute('data-url');
      if (!url) return;

      const load = async () => {
        try {
          const r = await fetch(url);
          if (!r.ok) { el.remove(); return; }
          const text = await r.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('[data-product-recommendations]');
          // No section / no products returned → remove the whole block, never show placeholders.
          if (!fresh || !fresh.querySelector('.card-product')) { el.remove(); return; }
          el.innerHTML = fresh.innerHTML;
        } catch (e) {
          el.remove();
        }
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          if (entries.some(en => en.isIntersecting)) { obs.disconnect(); load(); }
        }, { rootMargin: '400px' });
        io.observe(el);
      } else {
        load();
      }
    }
  };

  /* ==========================================================
     Upgrade finish selector — choosing a finish on a multi-variant upgrade
     re-points its Add button to the selected variant.
     ========================================================== */
  const UpgradeSelect = {
    init () {
      document.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-upg-select]');
        if (!sel) return;
        const opt = sel.closest('.pdp-comp-opt');
        const btn = opt && opt.querySelector('[data-upg-add]');
        if (btn) btn.setAttribute('data-variant-id', sel.value);
      });
    }
  };

  /* ==========================================================
     Lightbox — click any product / companion / upgrade thumbnail to view it larger.
     ========================================================== */
  const Lightbox = {
    init () {
      const bigger = (src) => (/[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$11600') : src);
      const close = () => { const ov = document.querySelector('.img-lightbox'); if (ov) { ov.remove(); document.body.style.overflow = ''; } };
      document.addEventListener('click', (e) => {
        const img = e.target.closest('.pdp-comp-opt__img, .pdp-kit-list__img, .product-gallery__slide img');
        if (!img) return;
        e.preventDefault();
        close();
        const ov = document.createElement('div');
        ov.className = 'img-lightbox';
        const im = document.createElement('img');
        im.src = bigger(img.currentSrc || img.getAttribute('src') || '');
        im.alt = img.alt || '';
        ov.appendChild(im);
        ov.addEventListener('click', close);
        document.body.appendChild(ov);
        document.body.style.overflow = 'hidden';
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    }
  };

  /* ==========================================================
     Boot
     ========================================================== */
  function boot () {
    // Isolate each init so one component failing can never disable the others (esp. the cart).
    [Drawer, MobileNav, SearchModal, ProductForm, Gallery, Collection, StickyHeader, QuickAdd, Recommendations, UpgradeSelect, Lightbox].forEach(m => {
      try { m.init(); } catch (e) { if (window.console) console.error('[theme] init failed:', e); }
    });
    Cart.fetch().catch(() => {});
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) { try { Reveal.init(); } catch (e) {} }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.ToiletsEtc = { Cart, Drawer };
})();
